import { Medal } from "lucide-react";

import { GoalBadge } from "@/components/goal-badge";
import { MetricHead } from "@/components/metric-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScoreCell } from "@/components/score-cell";
import type { LeaderboardRow, RubricSpec } from "@/lib/api";
import {
  formatDegrees,
  formatError,
  formatRelative,
  formatSeconds,
  formatSteps,
} from "@/lib/format";
import { formatSpl } from "@/lib/scoring";
import { cn } from "@/lib/utils";

const MEDAL_COLOR = ["text-amber-500", "text-zinc-400", "text-amber-700"];

function Rank({ rank }: { rank: number }) {
  if (rank <= 3) {
    return (
      <span className="flex items-center gap-1.5 font-semibold">
        <Medal className={cn("size-4", MEDAL_COLOR[rank - 1])} aria-hidden />
        {rank}
      </span>
    );
  }
  return <span className="text-muted-foreground tabular-nums">{rank}</span>;
}

/**
 * Ranked standings.
 *
 * Score first, then the metrics it was made from, then the clock: the order of the
 * ranking key, so the table never invites "why is a slower time ranked higher?". The
 * metric columns carry the rubric's names -- distance and steps always, since they
 * rank; heading and SPL only when the rubric grades them -- and each is one line,
 * so every row is the same height. Distance and heading are blank for a miss: the
 * server does not say how near one came.
 */
export function LeaderboardTable({
  rows,
  currentEmail,
  rubric,
}: {
  rows: LeaderboardRow[];
  currentEmail?: string | null;
  rubric: RubricSpec;
}) {
  const showHeading = rubric.variables.includes("heading");
  const showSpl = rubric.variables.includes("spl");
  const max = rubric.max_points;
  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40">
            <TableHead className="w-16">#</TableHead>
            <TableHead>Team</TableHead>
            <TableHead className="hidden md:table-cell">Members</TableHead>
            <MetricHead metric="goal" />
            <MetricHead metric="score" className="text-right" />
            <MetricHead metric="steps" className="text-right" />
            {showSpl ? <MetricHead metric="spl" className="hidden text-right md:table-cell" /> : null}
            <MetricHead metric="distance" className="hidden text-right sm:table-cell" />
            {showHeading ? (
              <MetricHead metric="heading" className="hidden text-right md:table-cell" />
            ) : null}
            <MetricHead metric="time" className="hidden text-right lg:table-cell" />
            <TableHead className="hidden text-right sm:table-cell">
              Submitted
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const emails = row.group
              ? row.group.members.map((m) => m.email)
              : [row.best_member.email];
            const isMine = Boolean(currentEmail && emails.includes(currentEmail));

            return (
              <TableRow
                key={row.group?.id ?? row.best_member.email}
                className={cn(isMine && "bg-primary/5 hover:bg-primary/10")}
              >
                <TableCell>
                  <Rank rank={row.rank} />
                </TableCell>

                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {row.group?.name ?? row.best_member.name ?? row.best_member.email}
                    </span>
                    {!row.group ? (
                      <span className="text-muted-foreground text-xs">solo</span>
                    ) : null}
                    {isMine ? (
                      <span className="bg-primary/15 text-primary rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                        You
                      </span>
                    ) : null}
                  </div>
                </TableCell>

                <TableCell className="hidden md:table-cell">
                  <div className="text-muted-foreground text-sm">
                    {row.group ? (
                      row.group.members.map((m) => (
                        <span
                          key={m.email}
                          className={cn(
                            "mr-2 inline-block",
                            m.email === row.best_member.email && "text-foreground",
                          )}
                          // The team's score comes from a single member, so mark
                          // which one it was rather than implying a team average.
                          title={
                            m.email === row.best_member.email
                              ? `${m.email} — counted for the team`
                              : m.email
                          }
                        >
                          {m.name || m.email}
                        </span>
                      ))
                    ) : (
                      <span>{row.best_member.email}</span>
                    )}
                  </div>
                </TableCell>

                <TableCell>
                  <GoalBadge tier={row.reached_goal} />
                </TableCell>

                <TableCell className="text-right">
                  <ScoreCell points={row.points} max={max} />
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">
                  {formatSteps(row.nav_steps)}
                </TableCell>
                {showSpl ? (
                  <TableCell className="text-muted-foreground hidden text-right tabular-nums md:table-cell">
                    {formatSpl(row.spl)}
                  </TableCell>
                ) : null}
                <TableCell className="text-muted-foreground hidden text-right tabular-nums sm:table-cell">
                  {formatError(row.arrival?.distance_m ?? null)}
                </TableCell>
                {showHeading ? (
                  <TableCell className="text-muted-foreground hidden text-right tabular-nums md:table-cell">
                    {formatDegrees(row.arrival?.heading_deg ?? null)}
                  </TableCell>
                ) : null}
                <TableCell className="text-muted-foreground hidden text-right tabular-nums lg:table-cell">
                  {formatSeconds(row.wall_s)}
                </TableCell>
                <TableCell className="text-muted-foreground hidden text-right text-sm sm:table-cell">
                  {formatRelative(row.submitted_at)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
